// hamburger-toggle-control-factory.ts
import { ButtonElement } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { HamburgerToggleControl } from "appworks/ui/controls/standard/toggle/hamburger-toggle-control";
import { ControlFactory } from "../control-factory";
import { gameiomModel } from "gameiom/model/gameiom-model";

/**
 * Factory class for creating instances of `HamburgerToggleControl`. This class implements the `ControlFactory` interface.
 *
 * @property {ButtonElement[]} hamburgerMenuItems - An array of ButtonElement objects representing all the menu buttons included in the Hamburger Menu.
 *
 * @method filter(layer: Layers) - This method checks if a given layer has the necessary sprites to build a `HamburgerToggleControl` instance.
 * If the required elements are found, they are stored in the `hamburgerMenuItems` array for later use.
 * The method returns a boolean indicating whether the layer contains the necessary sprites.
 *
 * @method build(layer: Layers) - This method creates a new instance of `HamburgerToggleControl` based on the given layer.
 * It initializes the `HamburgerToggleControl` with an expand button, collapse button, and the hamburger menu items collected from the `filter` method.
 * It returns an array containing the created `HamburgerToggleControl` instances.
 *
 * When creating a Hamburger Menu, there are buttons like "sound_toggle_on", "sound_toggle_off", and "menu_toggle_expand" that should not collapse the menu when clicked.
 * These are specified with the property `shouldCollapseMenu` being set to `false`.
 */
export class HamburgerToggleControlFactory implements ControlFactory {
    protected readonly menuExpandButtonName = "menu_toggle_expand";
    protected readonly menuCollapseButtonName = "menu_toggle_collapse";
    protected readonly settingsButtonName = "ui_settings";
    protected readonly helpButtonName = "ui_paytable";
    protected readonly soundOnButtonName = "sound_toggle_on";
    protected readonly soundOffButtonName = "sound_toggle_off";

    protected hamburgerMenuItems: ButtonElement[] = [];

    /**
     * Checks if the given layer has the necessary sprites to build a hamburger toggle control, and stores the necessary FlexiButton elements for later use.
     *
     * @param {Layers} layer - The layer to filter.
     * @returns {boolean} - True if the layer has the necessary sprites, false otherwise.
     */
    public filter(layer: Layers) {
        return layer.getButton(this.menuExpandButtonName) != undefined && layer.getButton(this.menuCollapseButtonName) != undefined;
    }

    /**
     * Builds a hamburger toggle control based on the given layer.
     *
     * @param {Layers} layer - The layer to build the control from.
     * @returns {HamburgerToggleControl[]} - An array of HamburgerToggleControl objects built from the given layer.
     */
    public build(layer: Layers) {
        this.hamburgerMenuItems = [];
        this.hamburgerMenuItems.push(layer.getButton(this.helpButtonName));

        const settingsButton = layer.getButton(this.settingsButtonName);
        if (gameiomModel.shouldDisplaySettingsButton()) {
            this.hamburgerMenuItems.push(settingsButton);
        } else {
            settingsButton?.destroy();
        }

        const soundOnBtn = layer.getButton(this.soundOnButtonName);
        const soundOffBtn = layer.getButton(this.soundOffButtonName);
        const expandButton = layer.getButton(this.menuExpandButtonName);

        soundOnBtn.shouldCollapseMenu = false;
        soundOffBtn.shouldCollapseMenu = false;
        expandButton.shouldCollapseMenu = false;

        this.hamburgerMenuItems.push(soundOnBtn);
        this.hamburgerMenuItems.push(soundOffBtn);

        return [
            new HamburgerToggleControl(
                expandButton,
                layer.getButton(this.menuCollapseButtonName),
                this.hamburgerMenuItems
            )
        ];
    }
}